import { useState, useEffect, useCallback } from "react";
import Header from "./header";
import { useNavigate } from "react-router-dom";
import { END_POINT } from "../../helper/helper";
import "../dashscreen/BlogCss.css";

// ── Helper: extract display text from a feature that could be string OR object ──
const getFeatureText = (feature) => {
  if (typeof feature === "string") return feature;
  if (feature && typeof feature === "object") {
    // Common field names your DB might use
    return feature.text || feature.title || feature.name || feature.description || JSON.stringify(feature);
  }
  return String(feature);
};

// ── Helper: extract video URL from a feature object ──
const getFeatureVideo = (feature) => {
  if (typeof feature === "string") return null;
  if (feature && typeof feature === "object") {
    return feature.videoUrl || feature.video || feature.youtube || null;
  }
  return null;
};

const AIFeaturesScreen = () => {
  const navigate = useNavigate();

  const [videoModalTool, setVideoModalTool] = useState(null);
  const [videoModalUrl, setVideoModalUrl] = useState(null); // for per-feature video URLs
  const [homeDirectoryData, setHomeDirectoryData] = useState({
    categories: [],
    aiTools: [],
  });

  const openVideoModal = useCallback((tool, featureVideoUrl = null) => {
    setVideoModalTool(tool);
    setVideoModalUrl(featureVideoUrl); // if feature has its own video URL, use it
    document.body.style.overflow = "hidden";
  }, []);

  const closeVideoModal = useCallback(() => {
    setVideoModalTool(null);
    setVideoModalUrl(null);
    document.body.style.overflow = "auto";
  }, []);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeVideoModal(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeVideoModal]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const response = await fetch(END_POINT.GetHomeDirectoryData);
        const result = await response.json();
        if (!isMounted || !result?.success || !result?.data) return;
        setHomeDirectoryData({
          categories: Array.isArray(result.data.categories) ? result.data.categories : [],
          aiTools: Array.isArray(result.data.aiTools) ? result.data.aiTools : [],
        });
      } catch (error) {
        console.log("Failed to load home directory data", error);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  // Sort tools with new features by release date (most recent first)
  const toolsWithFeatures = [...homeDirectoryData.aiTools]
    .filter((tool) => tool.newFeatures)
    .sort((a, b) => new Date(b.newFeatures.releaseDate) - new Date(a.newFeatures.releaseDate));

  // Resolve the video URL to show in modal
  const modalVideoUrl = videoModalUrl || videoModalTool?.videos?.youtube || null;

  return (
    <>
      <Header />

      {/* ── FEATURES PAGE ── */}
      <div id="featuresPage" className="page active">
        <div className="container">

          <div className="features-page-header">
            <h1>✨ Latest AI Features & Updates</h1>
            <p>Stay ahead with the newest capabilities from top AI tools</p>
          </div>

          <div id="featuresContent">
            {toolsWithFeatures.length === 0 ? (
              <div className="no-results">
                <h3>No features data available</h3>
                <p>Check back soon for the latest AI updates.</p>
              </div>
            ) : (
              <div className="features-grid">
                {toolsWithFeatures.map((tool, idx) => {
                  const features = Array.isArray(tool.newFeatures?.features)
                    ? tool.newFeatures.features
                    : [];

                  return (
                    <div className="feature-card" key={idx}>

                      <div className="feature-card-header">
                        <div className="feature-tool-icon">{tool.icon}</div>
                        <div className="feature-tool-info">
                          <h3>{tool.name}</h3>
                          <p className="feature-tool-company">{tool.company}</p>
                          <div className="feature-release-date">
                            <span>🗓️</span>
                            Released: {tool.newFeatures.releaseDate}
                          </div>
                        </div>
                      </div>

                      <div className="feature-details">
                        <h4>Latest Updates:</h4>
                        <ul>
                          {features.map((feature, fIdx) => {
                            const text = getFeatureText(feature);
                            const featureVideo = getFeatureVideo(feature);
                            const hasVideo = featureVideo || tool.videos;

                            return (
                              <li key={fIdx}>
                                {/* Always render text as string */}
                                {text}

                                {/* Watch Video link */}
                                {hasVideo && (
                                  <a
                                    className="feature-link video"
                                    href="#"
                                    style={{ display: "inline-block", marginLeft: "0.5rem" }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      openVideoModal(tool, featureVideo || null);
                                    }}
                                  >
                                    Watch Video
                                  </a>
                                )}

                                {/* Website link (only if no video at all) */}
                                {!hasVideo && tool.website && (
                                  <a
                                    href={tool.website}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="feature-link website"
                                    style={{ display: "inline-block", marginLeft: "0.5rem" }}
                                  >
                                    Check Out the link
                                  </a>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>

                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── VIDEO MODAL ── */}
      <div
        id="videoModal"
        className={"video-modal" + (videoModalTool ? " active" : "")}
        onClick={(e) => { if (e.target.id === "videoModal") closeVideoModal(); }}
      >
        <div className="video-modal-content">

          <div className="video-modal-header">
            <div className="video-modal-title">
              <span id="modalToolIcon">{videoModalTool?.icon}</span>
              <span id="modalToolName">{videoModalTool?.name}</span>
            </div>
            <button className="video-modal-close" onClick={closeVideoModal}>&times;</button>
          </div>

          <div className="video-modal-body">

            <div className="video-container" id="videoContainer">
              {modalVideoUrl ? (
                <iframe
                  key={modalVideoUrl}
                  src={modalVideoUrl}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={videoModalTool?.name || "Video"}
                />
              ) : (
                <div style={{ padding: "4rem", textAlign: "center", color: "var(--muted)" }}>
                  <p>Video demo coming soon!</p>
                </div>
              )}
            </div>

            <div className="video-links" id="videoLinks">
              {videoModalTool?.videos?.tutorial && (
                <a href={videoModalTool.videos.tutorial} target="_blank" rel="noreferrer" className="video-link-btn">
                  <span className="video-link-icon">📺</span>
                  <span>YouTube Tutorial</span>
                </a>
              )}
              {videoModalTool?.videos?.demo && (
                <a href={videoModalTool.videos.demo} target="_blank" rel="noreferrer" className="video-link-btn">
                  <span className="video-link-icon">📚</span>
                  <span>Documentation</span>
                </a>
              )}
              {videoModalTool?.website && (
                <a href={videoModalTool.website} target="_blank" rel="noreferrer" className="video-link-btn">
                  <span className="video-link-icon">🌐</span>
                  <span>Official Website</span>
                </a>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer>
        <div className="container">
          <div className="footer-content">
            <p>© 2026 AIHub. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default AIFeaturesScreen;